import React, { useState } from 'react';
import Text from 'antd/lib/typography/Text';
import { DefaultOptionType } from 'antd/es/select';
import Select from 'antd/lib/select';
import CheckableTag from 'antd/lib/tag/CheckableTag';
import { Button, ConfigProvider, Flex, Modal } from 'antd/lib';
import { antGrey, getAntdThemeTokens, runwayColors } from '@/components/styles/globalStyles';
import Avatar from 'antd/lib/avatar/avatar';
import { AppPlatform } from '@/types';
import { CloseOutlined, TeamOutlined } from '@ant-design/icons';
import { useThemeMode } from '@/components/styles/themeMode';
import antdTheme from 'antd/lib/theme';

type OrgOverviewTagOption = DefaultOptionType & {
    item: OrgOverviewTagInfo;
};

interface OrgOverviewTagSelectorProps {
    title: string;
    itemsById: Record<string, OrgOverviewTagInfo>;
    selectedTags: Record<string, string>;
    setSelectedTags: (value: Record<string, string>) => void;
    onTagChange?: (checked: boolean, item: OrgOverviewTagInfo) => void;
}

export interface OrgOverviewTagInfo {
    id: string;
    name: string;
    imageUrl?: string;
    platform?: AppPlatform;
    appId?: string;
    appIds?: string[];
    isUser?: boolean;
    isTeam?: boolean;
    isDestination?: boolean;
}

export function renderTagSelectorSection({
    title,
    itemsById,
    selectedTags,
    setSelectedTags,
    onTagChange,
}: {
    title: string;
    itemsById: Record<string, OrgOverviewTagInfo>;
    selectedTags: Record<string, string>;
    setSelectedTags: (value: Record<string, string>) => void;
    onTagChange?: (checked: boolean, item: OrgOverviewTagInfo) => void;
}) {
    if (!itemsById || !Object.keys(itemsById).length) {
        return null;
    }

    return (
        <div
            style={{
                display: 'flex',
                justifyContent: 'flex-start',
                flexDirection: 'column',
                paddingTop: 16,
            }}
        >
            <Text
                style={{
                    fontWeight: 400,
                    color: antGrey.grey6,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    fontSize: 12,
                }}
            >
                {title}
            </Text>
            <OrgOverviewTagSelector
                title={title}
                itemsById={itemsById}
                selectedTags={selectedTags}
                setSelectedTags={setSelectedTags}
                onTagChange={onTagChange}
            />
        </div>
    );
}

export const OrgOverviewTagSelector = ({
    title,
    itemsById,
    selectedTags,
    setSelectedTags,
    onTagChange,
}: OrgOverviewTagSelectorProps) => {
    const [filtersVisible, setFiltersVisible] = useState(false);
    const openModal = () => setFiltersVisible(true);
    const closeModal = () => setFiltersVisible(false);

    const { mode } = useThemeMode();
    const isDark = mode === 'dark';

    const getTagSelectorOptions = (): OrgOverviewTagOption[] => {
        if (!selectedTags) {
            return [];
        }

        return Object.values(itemsById).map((item) => ({
            label: (
                <Flex
                    align="center"
                    style={{
                        gap: 6,
                    }}
                >
                    {item.imageUrl || item.isUser ?
                        <Avatar
                            shape="square"
                            size={12}
                            src={item.imageUrl}
                            style={{
                                borderRadius: 6,
                                border: 'none',
                                flexShrink: 0,
                                backgroundColor:
                                    selectedTags[item.id] ? runwayColors.primaryLight : runwayColors.primary,
                                color: runwayColors.white,
                            }}
                        >
                            {item.name?.trim().charAt(0).toUpperCase()}
                        </Avatar>
                    : item.isTeam ?
                        <TeamOutlined />
                    :   null}
                    <Text style={{ color: 'inherit' }} ellipsis={{ tooltip: true }}>
                        {item.name}
                    </Text>
                    {selectedTags[item.id] ?
                        <CloseOutlined style={{ fontSize: 12 }} />
                    :   null}
                </Flex>
            ),
            value: item.id,
            item,
        }));
    };

    const renderTag = (option: OrgOverviewTagOption) =>
        option.value ?
            <CheckableTag
                checked={!!selectedTags[option.value]}
                onChange={(checked) => toggleOption(checked, option)}
                className="tag-selector-tag"
                style={{
                    maxWidth: '100%',
                    marginBottom: 8,
                    marginRight: 6,
                    borderRadius: 2,
                    padding: '1px 8px',
                }}
            >
                {option.label}
            </CheckableTag>
        :   null;

    const toggleOption = (checked: boolean, option: OrgOverviewTagOption) => {
        onTagChange?.(checked, option.item);

        if (onTagChange || !option.value) {
            return;
        }

        if (checked) {
            setSelectedTags({ ...selectedTags, [option.value]: option.value as string });
        } else {
            const clone = Object.assign({}, selectedTags);
            delete clone[option.value];
            setSelectedTags(clone);
        }
    };

    const allOptions = getTagSelectorOptions();
    const optionsById = allOptions.reduce(
        (acc, option) => {
            acc[option.value as string] = option;
            return acc;
        },
        {} as Record<string, OrgOverviewTagOption>,
    );
    const shouldViewMore = allOptions.length > 5;
    const optionsToDisplay = shouldViewMore ? allOptions.slice(0, 5) : allOptions;

    return (
        <>
            <div style={{ display: 'flex', flexWrap: 'wrap', flexDirection: 'row', marginTop: 10 }}>
                {optionsToDisplay.map((option) => renderTag(option))}
            </div>
            {shouldViewMore ?
                <>
                    <Button
                        type="link"
                        style={{
                            display: 'flex',
                            justifyContent: 'flex-start',
                            padding: 0,
                            color: runwayColors.primaryLight,
                        }}
                        onClick={openModal}
                    >
                        view more...
                    </Button>
                    <ConfigProvider
                        theme={{
                            algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
                            token: getAntdThemeTokens(isDark ? 'dark' : 'light'),
                        }}
                    >
                        <Modal
                            title={title}
                            open={filtersVisible}
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                            }}
                            onClose={closeModal}
                            onOk={closeModal}
                            onCancel={closeModal}
                            cancelButtonProps={{ style: { display: 'none' } }}
                        >
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    paddingLeft: 16,
                                    paddingRight: 16,
                                    paddingBottom: 18,
                                    maxWidth: 450,
                                }}
                            >
                                <Select
                                    mode="multiple"
                                    style={{ minWidth: 200, flex: 1 }}
                                    value={Object.keys(selectedTags)}
                                    options={allOptions}
                                    optionLabelProp="label"
                                    showSearch
                                    filterOption={(input, option) =>
                                        option?.item.name?.toLowerCase().includes(input.toLowerCase()) ?? false
                                    }
                                    onChange={(nextValues) => {
                                        const previousValues = new Set(Object.keys(selectedTags));
                                        const nextValueSet = new Set(nextValues as string[]);

                                        for (const value of nextValues as string[]) {
                                            if (!previousValues.has(value)) {
                                                toggleOption(true, optionsById[value]);
                                            }
                                        }

                                        for (const value of previousValues) {
                                            if (!nextValueSet.has(value)) {
                                                toggleOption(false, optionsById[value]);
                                            }
                                        }
                                    }}
                                />
                            </div>
                        </Modal>
                    </ConfigProvider>
                </>
            :   null}
        </>
    );
};
